import { graphql } from '~/graphql/generated/gql';

export const conversationStatsFragment = graphql(`
  fragment conversationStatsFields on AdminCoachSessionStats {
    id
    status
    endedAt
    createdAt
    transcriptEventCount
    assistantEventCount
    userEventCount
    toolCallCount
    activityNavigationCount
    learningAssetCompletionCount
  }
`);

export const conversationReviewEventFragment = graphql(`
  fragment conversationReviewEventFields on CoachSessionEvent {
    __typename
    seq
    createdAt
    ... on CoachAssistantChatResponseEvent {
      id
      eventData {
        parts {
          __typename
          ... on CoachAssistantMessagePart { text }
          ... on CoachAssistantRecommendationsPart {
            set {
              __typename
              ... on CoachContentRecommendationSet {
                recommendations {
                  __typename
                  ... on CoachLearningAssetRecommendation {
                    id name slug learningAssetType duration coins isSaved isCompleted
                  }
                  ... on CoachLearningAssetCollectionRecommendation {
                    id name slug collectionType completedCount totalCount duration coins isSaved partnerName
                  }
                }
              }
              ... on CoachJobRecommendationSet {
                recommendations { id name isSaved }
              }
              ... on CoachInstitutionRecommendationSet {
                recommendations { id name isSaved }
              }
              ... on CoachActionRecommendationSet {
                recommendations { id name navigation }
              }
              ... on CoachIndustryRecommendationSet {
                recommendations { id name isSaved }
              }
              ... on CoachSurveyRecommendationSet {
                recommendations { id name contextType coins externalLaunchPath }
              }
            }
          }
          ... on CoachAssistantCrisisPart {
            country
            emphasiseEmergency
            emergency { slug name phone sms smsKeyword url emergency }
            services { slug name phone sms smsKeyword url emergency }
          }
        }
        askUserQuestion {
          __typename
          ... on CoachPlainQuestion { question selectType options { id label } }
          ... on CoachEndSessionQuestion { question options { id label role } }
          ... on CoachGoalSwitchQuestion {
            question
            options { id label role }
            newGoalTitle
            newGoal
            handoff
            provenance
          }
          ... on CoachGoalSetQuestion { question goalTitle goal options { id label role } }
          ... on CoachStartersQuestion { question assignedLessonsTotal options { id label subtitle icon school } }
        }
      }
    }
    ... on CoachUserChatInputEvent {
      id
      eventData {
        userMessage
        inputType
      }
    }
    ... on CoachQuestionAnswerEvent {
      id
      eventData {
        questionEventId
        selectedOptionIds
        selectedLabels
        role
      }
    }
    ... on CoachSessionStartersEvent {
      id
      eventData {
        question {
          __typename
          ... on CoachPlainQuestion { question selectType options { id label } }
          ... on CoachEndSessionQuestion { question options { id label role } }
          ... on CoachGoalSwitchQuestion {
            question
            options { id label role }
            newGoalTitle
            newGoal
            handoff
            provenance
          }
          ... on CoachGoalSetQuestion { question goalTitle goal options { id label role } }
          ... on CoachStartersQuestion { question assignedLessonsTotal options { id label subtitle icon school } }
        }
      }
    }
    ... on CoachToolCallEvent {
      id
      eventData {
        toolName
        toolCallId
        arguments
        round
        callIndex
        callsInRound
      }
    }
    ... on CoachToolResponseEvent {
      id
      eventData {
        toolName
        toolCallId
        response
        round
        callIndex
        callsInRound
        durationMs
      }
    }
    ... on CoachActivityNavigationEvent {
      id
      eventData {
        activity
        activityArgs
      }
    }
    ... on CoachCrisisResponseEvent {
      id
      eventData {
        signal
        level
        country
        cardShown
      }
    }
    ... on CoachActivityInfoEvent {
      id
      eventData {
        infoType
        activityNavigationEventId
        described
        payload
      }
    }
    ... on CoachGoalChangedEvent {
      id
      eventData {
        kind
        goalTitle
        previousGoalTitle
      }
    }
  }
`);

export const conversationReviewAnnotationFragment = graphql(`
  fragment conversationReviewAnnotationFields on AdminCoachSessionAnnotation {
    id
    sessionId
    eventId
    anchorKind
    verdict
    body
    author {
      id
      name
    }
    createdAt
    updatedAt
  }
`);

export const GET_COACH_REVIEW_STATS = graphql(`
  query getCoachReviewStats($filters: CoachSessionListFilters) {
    coachSessionReviewStats(filters: $filters) {
      totalConversations
      totalUsers
      totalLearningAssetCompletions
      totalActivityNavigations
      totalCrisisResponses
    }
  }
`);

export const GET_COACH_SESSION_LIST = graphql(`
  query getCoachSessionList(
    $filters: CoachSessionListFilters
    $orderBy: [CoachSessionListOrderBy!]
    $first: Int!
    $page: Int
  ) {
    coachSessionList(filters: $filters, orderBy: $orderBy, first: $first, page: $page) {
      paginatorInfo {
        ...PaginatorInfoFields
      }
      data {
        id
        userId
        status
        goalTitle
        endReason
        createdAt
        lastEventAt
        userEventCount
        activityNavigationCount
        lessonId
        isSchoolUser
        annotationCount
        reviewers {
          id
          name
          noteCount
        }
      }
    }
  }
`);

// The counts card reads the roll for one student rather than the transcript query, so the read it
// logs is a counts view of that student and never looks like a transcript open.
export const GET_USER_CONVERSATION_STATS = graphql(`
  query getUserConversationStats($userId: ID!) {
    coachSessionUsers(filters: { userId: $userId }, first: 1) {
      data {
        id
        coachSessions {
          ...conversationStatsFields
        }
      }
    }
  }
`);

export const GET_COACH_SESSION_RAIL = graphql(`
  query getCoachSessionRail($userId: ID!, $page: Int!) {
    coachSessions(filters: { userId: $userId }, first: 20, page: $page, orderBy: [{ column: CREATED_AT, order: DESC }, { column: ID, order: DESC }]) {
      data {
        id
        createdAt
        endedAt
        lastEventAt
        status
        endReason
        goalTitle
        userEventCount
        activityNavigationCount
        lessonId
        isSchoolUser
        annotationCount
        reviewers {
          id
          name
          noteCount
        }
      }
      paginatorInfo {
        ...PaginatorInfoFields
      }
    }
  }
`);

export const GET_ADMIN_COACH_SESSION = graphql(`
  query getAdminCoachSession($id: ID!) {
    adminCoachSession(id: $id) {
      id
      userId
      status
      createdAt
      updatedAt
      endedAt
      lastEventAt
      endReason
      goalTitle
      goal
      goalSource
      userEventCount
      activityNavigationCount
      summary
      lessonId
      isSchoolUser
      launchedViaSchoolsDoor
      annotationCount
      reviewers {
        id
        name
        noteCount
      }
      compactions {
        id
        compaction
        compactedSeqs
        createdAt
      }
      events {
        ...conversationReviewEventFields
      }
      annotations {
        ...conversationReviewAnnotationFields
      }
    }
  }
`);

export const CREATE_COACH_SESSION_ANNOTATION_MUTATION = graphql(`
  mutation createCoachSessionAnnotation($input: CreateCoachSessionAnnotationInput!) {
    createCoachSessionAnnotation(input: $input) {
      annotation {
        ...conversationReviewAnnotationFields
      }
      session {
        id
        annotationCount
        reviewers {
          id
          name
          noteCount
        }
      }
    }
  }
`);

export const UPDATE_COACH_SESSION_ANNOTATION_MUTATION = graphql(`
  mutation updateCoachSessionAnnotation($input: UpdateCoachSessionAnnotationInput!) {
    updateCoachSessionAnnotation(input: $input) {
      annotation {
        ...conversationReviewAnnotationFields
      }
      session {
        id
        annotationCount
        reviewers {
          id
          name
          noteCount
        }
      }
    }
  }
`);

export const DELETE_COACH_SESSION_ANNOTATION_MUTATION = graphql(`
  mutation deleteCoachSessionAnnotation($input: DeleteCoachSessionAnnotationInput!) {
    deleteCoachSessionAnnotation(input: $input) {
      id
      sessionId
      session {
        id
        annotationCount
        reviewers {
          id
          name
          noteCount
        }
      }
    }
  }
`);
